import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { LevelBadge } from "@/components/LevelBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SKILLS, levelFor } from "@/lib/framework";
import { toast } from "sonner";
import { FinePrint } from "@/components/FinePrint";
import { Building2, ShieldCheck, Loader2, Star, Trash2, Briefcase, ListChecks, Lightbulb, CalendarClock, Mail, HelpCircle } from "lucide-react";

export const EMPLOYER_NAV = [
  { to: "/app/employer", label: "Talent & 360", icon: Building2 },
  { to: "/app/employer/jobs", label: "Job Board", icon: Briefcase },
  { to: "/app/tasks", label: "My Tasks", icon: ListChecks },
  { to: "/app/suggestions", label: "Submit a Suggestion", icon: Lightbulb },
  { to: "/app/help", label: "Help & Documentation", icon: HelpCircle },
];

export default function EmployerHome() {
  const [students, setStudents] = useState(null);
  const [active, setActive] = useState(null); // student being reviewed
  const [ratings, setRatings] = useState({});
  const [relationship, setRelationship] = useState("Internship Supervisor");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/employer/students").then(({ data }) => setStudents(data));
  useEffect(() => { load(); }, []);

  const openReview = (s) => {
    setActive(s);
    setRatings(Object.fromEntries(SKILLS.map((sk) => [sk.id, 3])));
    setRelationship("Internship Supervisor");
    setComment("");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/employer/reviews", {
        student_id: active.id, skill_ratings: ratings, relationship, comment,
      });
      toast.success(`Verified 360 submitted for ${active.name}`);
      setActive(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardShell title="Employer Portal" subtitle="Jordan Blake · Acme Corporation — opt-in talent & verified 360s" nav={EMPLOYER_NAV}>
      <Card className="mb-6 border-[#78be21]/40 bg-[#78be21]/5 p-4">
        <p className="flex items-center gap-2 text-sm text-slate-700">
          <ShieldCheck className="h-5 w-5 text-[#5fa114]" />
          You only see students who have <strong>opted in</strong>. Your 360 review becomes a separate, verified, reversible skill signal.
        </p>
      </Card>

      {!students && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {students && (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {students.map((s, i) => (
            <Card key={s.id} className="flex flex-col p-5 card-elevate" data-testid={`employer-student-${i}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-heading text-lg font-bold text-[#003057]">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.program}</p>
                </div>
                <LevelBadge level={s.level} />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <span className="font-mono-num text-2xl font-bold text-[#005291]">{s.tsb.toFixed(2)}</span>
                <span className="text-xs text-slate-400">TSB · {s.tsb_100}/100</span>
                {s.work_style && <span className="ml-auto rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{s.work_style}</span>}
              </div>
              <div className="mt-3 space-y-1">
                {s.top_skills.map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{t.name}</span>
                    <span className="font-mono-num font-bold text-slate-800">{t.score.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {s.about_me && (
                <p className="mt-3 line-clamp-3 border-l-2 border-[#78be21]/50 pl-3 text-xs italic text-slate-500" data-testid={`employer-about-${i}`}>
                  “{s.about_me}”
                </p>
              )}
              {(s.calendar_url || s.contact_email) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {s.calendar_url && (
                    <a href={s.calendar_url} target="_blank" rel="noreferrer" data-testid={`employer-booktime-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#003057]/20 px-2.5 py-1 text-xs font-semibold text-[#005291] transition-colors hover:bg-[#003057]/5">
                      <CalendarClock className="h-3.5 w-3.5" /> Book time
                    </a>
                  )}
                  {s.contact_email && (
                    <a href={`mailto:${s.contact_email}`} data-testid={`employer-email-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#003057]/20 px-2.5 py-1 text-xs font-semibold text-[#005291] transition-colors hover:bg-[#003057]/5">
                      <Mail className="h-3.5 w-3.5" /> Email
                    </a>
                  )}
                </div>
              )}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-slate-400">{s.review_count} review(s)</span>
                <Button size="sm" onClick={() => openReview(s)} data-testid={`employer-review-btn-${i}`}
                  className="rounded-full bg-[#003057] font-semibold hover:bg-[#004275]">
                  <Star className="mr-1.5 h-3.5 w-3.5" /> Submit 360
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#003057]">Employer 360 · {active?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Relationship</Label>
              <Input value={relationship} onChange={(e) => setRelationship(e.target.value)} className="mt-1.5" data-testid="employer-relationship" />
            </div>
            <div className="space-y-3">
              <Label>Rate observed skills (1.0 – 4.0)</Label>
              {SKILLS.map((sk) => (
                <div key={sk.id}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{sk.name}</span>
                    <span className="font-mono-num font-bold" style={{ color: levelFor(ratings[sk.id] || 3).hex }}>
                      {(ratings[sk.id] ?? 3).toFixed(1)}
                    </span>
                  </div>
                  <Slider data-testid={`employer-slider-${sk.id}`} min={1} max={4} step={0.1}
                    value={[ratings[sk.id] ?? 3]}
                    onValueChange={(v) => setRatings((r) => ({ ...r, [sk.id]: v[0] }))} className="mt-1.5" />
                </div>
              ))}
            </div>
            <div>
              <Label>Comment (optional)</Label>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1.5"
                placeholder="Observed strengths in the role…" data-testid="employer-comment" />
            </div>
            <Button onClick={submit} disabled={submitting} data-testid="employer-360-submit-button"
              className="w-full rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit verified 360"}
            </Button>
            <p className="text-center text-xs text-slate-400">Blends at 25% weight · fully reversible · audited</p>
            <FinePrint>
              The Employer 360 is a separate, employer-reported signal. It is individually weighted (25% prototype value),
              clearly labeled, traceable in the audit trail, and reversible — it never overwrites coursework-derived scores.
            </FinePrint>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
